import { useEffect, useRef, useState, useLayoutEffect } from "react";

interface Photo {
  id: string;
  thumbnail_url: string;
  full_url: string;
  width: number;
  height: number;
}

interface Props {
  photos: Photo[];
  onSelect: (idx: number) => void;
  targetRowHeight?: number;
  gap?: number;
}

interface Row {
  items: { photo: Photo; idx: number; width: number; height: number }[];
}

const JustifiedGallery = ({ photos, onSelect, targetRowHeight, gap = 8 }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const isMobile = containerWidth > 0 && containerWidth < 640;
  const rowHeight = targetRowHeight ?? (isMobile ? 180 : 280);

  const rows: Row[] = [];
  if (containerWidth > 0 && photos.length > 0) {
    let current: Row = { items: [] };
    let currentAspectSum = 0;

    photos.forEach((photo, idx) => {
      const aspect = photo.width && photo.height ? photo.width / photo.height : 1.5;
      current.items.push({ photo, idx, width: aspect * rowHeight, height: rowHeight });
      currentAspectSum += aspect;

      const totalGap = gap * (current.items.length - 1);
      const projectedWidth = currentAspectSum * rowHeight + totalGap;

      if (projectedWidth >= containerWidth) {
        // Justify row
        const availableWidth = containerWidth - totalGap;
        const scaledHeight = availableWidth / currentAspectSum;
        current.items = current.items.map((it) => ({
          ...it,
          width: (it.photo.width / it.photo.height || 1.5) * scaledHeight,
          height: scaledHeight,
        }));
        rows.push(current);
        current = { items: [] };
        currentAspectSum = 0;
      }
    });

    if (current.items.length) {
      // Last row: keep target height, don't overstretch
      rows.push(current);
    }
  }

  return (
    <div ref={containerRef} className="w-full">
      {rows.map((row, ri) => (
        <div
          key={ri}
          className="flex"
          style={{ marginBottom: gap, gap: `${gap}px` }}
        >
          {row.items.map((it) => (
            <button
              key={it.photo.id}
              onClick={() => onSelect(it.idx)}
              className="relative overflow-hidden bg-muted group rounded-sm"
              style={{ width: it.width, height: it.height, flexShrink: 0 }}
            >
              <img
                src={it.photo.thumbnail_url}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover transition-all duration-500 group-hover:scale-[1.03] group-hover:brightness-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

export default JustifiedGallery;
